import React, { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import {
  Game,
  CreateGameRequest,
  UpdateGameRequest,
} from "../api/services/games/type";
import { useGameManagement } from "../hooks/useGames";
import { GameForm } from "../components/games/GameForm";
import { GameTable } from "../components/games/GameTable";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Pagination } from "../components/common/Pagination";
import toast from "react-hot-toast";

export const Games: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | undefined>(undefined);
  const itemsPerPage = 25;

  const {
    games,
    isLoading,
    isError,
    error,
    createGame,
    updateGame,
    deleteGame,
    isCreating,
    isUpdating,
    refreshGames,
  } = useGameManagement({
    limit: 1000,
  });

  const filteredGames = games.filter((game) => {
    if (!searchTerm) return true;
    return game.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(filteredGames.length / itemsPerPage);
  const paginatedGames = filteredGames.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateGame = () => {
    setEditingGame(undefined);
    setShowForm(true);
  };

  const handleEditGame = (game: Game) => {
    setEditingGame(game);
    setShowForm(true);
  };

  const handleFormSubmit = (data: CreateGameRequest | UpdateGameRequest) => {
    if (editingGame) {
      updateGame(
        { gameId: editingGame.id, data },
        {
          onSuccess: () => {
            toast.success("Game updated successfully!");
            setShowForm(false);
            setEditingGame(undefined);
          },
          onError: (error) => {
            toast.error(`Failed to update game: ${error.message}`);
          },
        },
      );
    } else {
      createGame(data as CreateGameRequest, {
        onSuccess: () => {
          toast.success("Game created successfully!");
          setShowForm(false);
        },
        onError: (error) => {
          toast.error(`Failed to create game: ${error.message}`);
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<string | null>(null);

  const handleDeleteGame = (gameId: string) => {
    setGameToDelete(gameId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (gameToDelete) {
      deleteGame(gameToDelete, {
        onSuccess: () => {
          toast.success("Game deleted successfully!");
          setDeleteDialogOpen(false);
          setGameToDelete(null);
        },
        onError: (error) => {
          toast.error(`Failed to delete game: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setGameToDelete(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingGame(undefined);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Games
            </h3>
            <p className="text-red-300">
              {error?.message || "Failed to load games. Please try again."}
            </p>
            <button
              onClick={() => refreshGames()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Add Button */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search games by name..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleCreateGame}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add Game
            </button>
          </div>
        </div>

        {/* Games Table */}
        <div className="mb-6">
          <GameTable
            games={paginatedGames}
            onEdit={handleEditGame}
            onDelete={handleDeleteGame}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* Game Form Modal */}
        {showForm && (
          <GameForm
            game={editingGame}
            isLoading={isCreating || isUpdating}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Game"
          description="Are you sure you want to delete this game? This action cannot be undone."
          icon={<Trash2 className="h-8 w-8 text-white" />}
          iconBgColor="bg-red-600"
          confirmColor="bg-red-600 hover:bg-red-700"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};
